import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { BillingHistory, BillingHistoryReqBody } from '../../../types/history';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useBillingActions = (
  whichAction: 'billingInfoList' | 'billingInfoByAssociateId' | '',
  associateId?: number
) => {
  // query function to fetch billing info lists declared here
  const getBillingInfoListHandler = useQuery({
    queryKey: ['billing-info-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<BillingHistory>>> => {
      const response = await getPrivateAxiosInstance('finance').get(
        '/associate/billing/info/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'billingInfoList'),
  });

  const getBillingInfoByAssociateIdHandler = useQuery({
    queryKey: ['billing-info-by-associate-id', associateId],
    queryFn: async (): Promise<AcademiaResponse<Array<BillingHistory>>> => {
      const response = await getPrivateAxiosInstance('finance').get(
        `/billing/info/of/associate/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(
      whichAction === 'billingInfoByAssociateId' && !!associateId
    ),
  });

  // mutation function to create new billing info declared here
  const createBillingInfoHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: BillingHistoryReqBody;
    }): Promise<AcademiaResponse<BillingHistory>> => {
      const response = await getPrivateAxiosInstance('finance').post(
        '/associate/billing/info/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update existing billing info declared here
  const updateBillingInfoHandler = useMutation({
    mutationFn: async ({
      billId,
      body,
    }: {
      billId: number;
      body: BillingHistoryReqBody;
    }): Promise<AcademiaResponse<BillingHistory>> => {
      const response = await getPrivateAxiosInstance('finance').patch(
        `/associate/billing/info/${billId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete existing billing info declared here
  const deleteBillingInfoHandler = useMutation({
    mutationFn: ({
      billId,
    }: {
      billId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('finance').delete(
        `/associate/billing/info/${billId}/delete/`
      ),
  });

  return {
    getBillingInfoListHandler,
    getBillingInfoByAssociateIdHandler,
    createBillingInfoHandler,
    updateBillingInfoHandler,
    deleteBillingInfoHandler,
  };
};

export default useBillingActions;
